/**
 * Chat Room Service
 * Handles chat room operations: listing, creating, fetching rooms
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/services/logger';
import { safeRandomUUID } from '@/lib/utils/safeUuid';
import type { Database } from '@/integrations/supabase/types';

type ChatRoom = Database['public']['Tables']['chat_rooms']['Row'];
type ChatMessage = Database['public']['Tables']['chat_messages']['Row'];
type ChatParticipant = Database['public']['Tables']['chat_participants']['Row'];

export interface ChatRoomWithDetails extends ChatRoom {
  lastMessage?: ChatMessage | null;
  unreadCount: number;
  participants: ChatParticipant[];
  otherParticipant?: {
    userId: string;
    displayName?: string;
    avatarUrl?: string;
  };
}

export const chatRoomService = {
  /**
   * Get all chat rooms for a user
   * @param userId - The user ID to fetch chat rooms for
   */
  async getChatRooms(userId: string): Promise<{ data: ChatRoomWithDetails[] | null; error: Error | null }> {
    try {
      if (!userId) {
        return { data: null, error: new Error('User not authenticated') };
      }

      // Single RPC call replaces 40+ individual queries
      const { data: rows, error: rpcError } = await supabase.rpc('fn_chat_rooms_list', {
        p_user_id: userId,
      });

      if (rpcError) throw rpcError;

      const roomsWithDetails: ChatRoomWithDetails[] = (rows || []).map((row: any) => ({
        id: row.room_id,
        context_entity_type: row.context_entity_type,
        context_entity_id: row.context_entity_id,
        kind: row.kind,
        creator_user_id: row.creator_user_id,
        last_message_at: row.last_message_at,
        created_at: row.room_created_at,
        updated_at: row.room_created_at,
        deleted_at: null,
        lastMessage: row.last_message_content ? {
          id: '',
          chat_room_id: row.room_id,
          content: row.last_message_content,
          content_type: row.last_message_type || 'text',
          content_type_codifier_key: 'chat_message_content_type',
          sender_user_id: row.last_message_sender_id,
          created_at: row.last_message_created_at,
          updated_at: row.last_message_created_at,
          deleted_at: null,
          media_asset_id: null,
        } : null,
        unreadCount: Number(row.unread_count) || 0,
        participants: [],
        otherParticipant: row.other_user_id ? {
          userId: row.other_user_id,
        } : undefined,
      }));

      return { data: roomsWithDetails, error: null };
    } catch (error) {
      logger.error('Error fetching chat rooms', {
        context: { feature: 'chat', function_name: 'getChatRooms' },
        error: error as Error,
        error_code: 'chat_rooms_fetch_failed',
        error_type: 'unexpected',
      });
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get chat room by ID
   */
  async getChatRoom(roomId: string): Promise<{ data: ChatRoomWithDetails | null; error: Error | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: new Error('User not authenticated') };
      }

      const { data: room, error } = await supabase
        .from('chat_rooms')
        .select(`
          *,
          chat_participants (
            id,
            user_id,
            role,
            last_read_at,
            created_at,
            chat_room_id,
            role_codifier_key,
            deleted_at
          )
        `)
        .eq('id', roomId)
        .is('deleted_at', null)
        .single();

      if (error) throw error;

      const otherParticipant = room.chat_participants?.find(
        (p: ChatParticipant) => p.user_id !== user.id
      );

      return {
        data: {
          ...room,
          unreadCount: 0,
          participants: room.chat_participants || [],
          otherParticipant: otherParticipant ? {
            userId: otherParticipant.user_id,
          } : undefined,
        },
        error: null,
      };
    } catch (error) {
      logger.error('Error fetching chat room', {
        context: { feature: 'chat', function_name: 'getChatRoom' },
        error: error as Error,
        error_code: 'chat_room_fetch_failed',
        error_type: 'unexpected',
      });
      return { data: null, error: error as Error };
    }
  },

  /**
   * Create a chat room for an application (linked to listing)
   * @param userId - The adopter's user ID creating the chat
   */
  async createChatRoomForApplication(
    userId: string,
    listingId: string,
    curatorUserId: string
  ): Promise<{ data: ChatRoom | null; error: Error | null }> {
    try {
      if (!userId) {
        return { data: null, error: new Error('User not authenticated') };
      }

      if (!listingId) {
        return { data: null, error: new Error('Missing listingId') };
      }

      let room: ChatRoom | null = null;

      // Try to reuse the (unique) active room for this listing.
      // Find existing room created by THIS user for this listing
      const { data: existingRoom, error: existingRoomError } = await supabase
        .from('chat_rooms')
        .select('*')
        .eq('context_entity_type', 'listing')
        .eq('context_entity_id', listingId)
        .eq('kind', 'application')
        .eq('creator_user_id', userId)
        .is('deleted_at', null)
        .limit(1)
        .maybeSingle();

      if (existingRoomError) throw existingRoomError;

      if (existingRoom) {
        room = existingRoom;
      } else {
        const roomId = safeRandomUUID();
        const nowIso = new Date().toISOString();

        const { error: roomError } = await supabase.from('chat_rooms').insert([
          {
            id: roomId,
            context_entity_type: 'listing',
            context_entity_id: listingId,
            kind: 'application',
            creator_user_id: userId,
          },
        ]);

        if (roomError) {
          // If another request created the room concurrently, just fetch and proceed.
          if ((roomError as any)?.code !== '23505') throw roomError;

          const { data: racedRoom, error: racedRoomError } = await supabase
            .from('chat_rooms')
            .select('*')
            .eq('context_entity_type', 'listing')
            .eq('context_entity_id', listingId)
            .eq('kind', 'application')
            .eq('creator_user_id', userId)
            .is('deleted_at', null)
            .limit(1)
            .maybeSingle();

          if (racedRoomError) throw racedRoomError;
          if (!racedRoom) throw roomError;

          room = racedRoom;
        } else {
          room = {
            id: roomId,
            context_entity_type: 'listing',
            context_entity_id: listingId,
            kind: 'application',
            creator_user_id: userId,
            created_at: nowIso,
            updated_at: nowIso,
            deleted_at: null,
            last_message_at: null,
          };
        }
      }

      if (!room) {
        return { data: null, error: new Error('Failed to create or load chat room') };
      }

      // Ensure current user is a participant (ignore duplicates)
      const { error: selfPartError } = await supabase.from('chat_participants').insert([
        {
          chat_room_id: room.id,
          user_id: userId,
          role: 'adopter',
          role_codifier_key: 'chat_participant_role',
        },
      ]);

      // Only throw if it's not a duplicate key violation
      if (selfPartError && (selfPartError as any)?.code !== '23505') throw selfPartError;

      // Now add curator using RPC
      const { error: curatorPartError } = await supabase.rpc('add_chat_participant', {
        room_id: room.id,
        participant_user_id: curatorUserId,
        participant_role: 'curator',
        participant_role_codifier_key: 'chat_participant_role',
      });

      if (curatorPartError && (curatorPartError as any)?.code !== '23505') throw curatorPartError;

      // Send notification to curator about new application (only for NEW rooms)
      if (!existingRoom) {
        supabase.functions.invoke('notifications-send', {
          body: {
            user_id: curatorUserId,
            type: 'new_application',
            entity_type: 'chat_room',
            entity_id: room.id,
            payload: { 
              listing_id: listingId,
              adopter_id: userId,
            },
          },
        }).catch(() => {
          // Non-blocking - don't fail if notification fails
          logger.warn('Failed to send new_application notification', {
            context: { feature: 'chat', function_name: 'createChatRoomForApplication' },
            data: { roomId: room.id, curatorUserId },
            allowList: ['roomId', 'curatorUserId'],
            error_code: 'notification_send_failed',
            error_type: 'network',
          });
        });
      }

      return { data: room, error: null };
    } catch (error) {
      logger.error('Error creating chat room', {
        context: { feature: 'chat', function_name: 'createChatRoom' },
        error: error as Error,
        error_code: 'chat_room_create_failed',
        error_type: 'unexpected',
      });
      return { data: null, error: error as Error };
    }
  },

  /**
   * Create a direct chat between moderator/admin and a user
   */
  async createDirectChat(userId: string, targetUserId: string): Promise<{ data: ChatRoom | null; error: Error | null }> {
    try {
      if (!userId) {
        return { data: null, error: new Error('User not authenticated') };
      }

      if (userId === targetUserId) {
        return { data: null, error: new Error('Cannot create chat with yourself') };
      }

      // Check if a direct chat already exists between these two users
      const { data: existingRooms, error: existingError } = await supabase
        .from('chat_rooms')
        .select(`
          *,
          chat_participants (user_id)
        `)
        .eq('kind', 'direct')
        .is('deleted_at', null);

      if (existingError) throw existingError;

      // Find room where both users are participants
      const existingRoom = existingRooms?.find(room => {
        const participantIds = room.chat_participants?.map((p: { user_id: string }) => p.user_id) || [];
        return participantIds.includes(userId) && participantIds.includes(targetUserId);
      });

      if (existingRoom) {
        return { data: existingRoom, error: null };
      }

      // Create new direct chat room
      const roomId = safeRandomUUID();
      const nowIso = new Date().toISOString();

      const { error: roomError } = await supabase.from('chat_rooms').insert([
        {
          id: roomId,
          context_entity_type: 'user',
          context_entity_id: targetUserId,
          kind: 'direct',
          creator_user_id: userId,
        },
      ]);

      if (roomError) throw roomError;

      const room: ChatRoom = {
        id: roomId,
        context_entity_type: 'user',
        context_entity_id: targetUserId,
        kind: 'direct',
        creator_user_id: userId,
        created_at: nowIso,
        updated_at: nowIso,
        deleted_at: null,
        last_message_at: null,
      };

      // Add self as participant (RLS allows inserting own user_id)
      const { error: selfError } = await supabase.from('chat_participants').insert({
        chat_room_id: roomId,
        user_id: userId,
        role: 'moderator',
        role_codifier_key: 'chat_participant_role',
      });

      // Only throw if it's not a duplicate key violation
      if (selfError && (selfError as any)?.code !== '23505') throw selfError;

      // Add target user via RPC (creator can add participants)
      const { error: targetError } = await supabase.rpc('add_chat_participant', {
        room_id: roomId,
        participant_user_id: targetUserId,
        participant_role: 'user',
        participant_role_codifier_key: 'chat_participant_role',
      });

      if (targetError && (targetError as any)?.code !== '23505') throw targetError;

      return { data: room, error: null };
    } catch (error) {
      logger.error('Error creating direct chat', {
        context: { feature: 'chat', function_name: 'createDirectChat' },
        error: error as Error,
        error_code: 'direct_chat_create_failed',
        error_type: 'unexpected',
      });
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get total unread count across all rooms
   */
  async getTotalUnreadCount(userId: string): Promise<{ count: number; error: Error | null }> {
    try {
      const { data, error } = await supabase.rpc('fn_chat_unread_total', {
        p_user_id: userId,
      });
      if (error) throw error;

      return { count: typeof data === 'number' ? data : 0, error: null };
    } catch (error) {
      logger.error('Error getting total unread count', {
        context: { feature: 'chat', function_name: 'getTotalUnreadCount' },
        error: error as Error,
        error_code: 'unread_count_failed',
        error_type: 'unexpected',
      });
      return { count: 0, error: error as Error };
    }
  },
};

export default chatRoomService;
